import { type ReactNode } from "react";

export interface EvaluationStatus {
  id: number;
  status: string;
  definition: ReactNode;
  initiation: ReactNode;
  description: ReactNode;
  timeline: string;
  point: string;
}

export const evaluationStatusRows: EvaluationStatus[] = [
  {
    id: 1,
    status: "In Draft",
    definition: (
      <>
        A new evaluation proposal is being defined by the Lab, but not completed
        yet.
      </>
    ),
    initiation: (
      <>
        Set automatically at time of input by the CCTL to alert NIAP to an
        upcoming Evaluation.
      </>
    ),
    description: (
      <>
        <p>
          The Check-In form is available via the NIAP portal from the Lab&apos;s
          dashboard.
        </p>
        <p>
          The Lab must have the following to define and save a new draft
          proposal:
        </p>
        <ul>
          <li>
            <b>Active NIAP Portal Account (Lab)</b>
          </li>
          <li>
            <b>Product Name</b> (required field on the Check-In form)
          </li>
          <li>
            <b>Vendor/Company Name</b> (required field on the Check-In form)
          </li>
        </ul>
        <p>
          * It is not a guarantee that projects in this state will reach
          proposal. It is at the lab&apos;s discretion to advance or not.
        </p>
      </>
    ),
    timeline: "No Time Limit",
    point: "N/A",
  },
  {
    id: 2,
    status: "Pre-Proposal",
    definition: (
      <>
        The Lab has completed an evaluation proposal and submitted it to the
        Vendor for signature. NIAP has not yet received it.
      </>
    ),
    initiation: (
      <>
        Set by the CCTL once check-in form is complete and Check-In package &
        EAR (if required) is submitted to the vendor for signature.
      </>
    ),
    description: (
      <>
        <p>
          Lab must have submitted the following to the vendor for signature:
        </p>
        <ul>
          <li>
            <b>Completed Check-In (F8002a) Form</b>
          </li>
        </ul>
        <p>and attached:</p>
        <ul>
          <li>
            <b>Draft Security Target</b>
          </li>
          <li>
            <b>Entropy Assessment Report (EAR) (if required)</b>
          </li>
        </ul>
        <p>
          * Projects in pre-proposal are viewable from the associated Vendors
          portal dashboard. Labs are required to submit Vendor Account requests
          to NIAP.
        </p>
      </>
    ),
    timeline: "No Time Limit",
    point: "N/A",
  },
  {
    id: 3,
    status: "Proposed",
    definition: (
      <>
        The Lab and Vendor have completed an evaluation proposal and NIAP has
        been notified, but it is not yet accepted.
      </>
    ),
    initiation: (
      <>
        Set automatically after Vendor signs off on the Check-In package and
        delivers to NIAP for review.
      </>
    ),
    description: (
      <>
        <p>NIAP has received the following:</p>
        <ul>
          <li>
            <b>Vendor signed Check-In (F8002a) Form</b>
          </li>
        </ul>
        <p>with attached vendor approved:</p>
        <ul>
          <li>
            <b>Draft Security Target</b>
          </li>
          <li>
            <b>Entropy Assessment Report (EAR) if required</b>
          </li>
        </ul>
        <p>During proposal, NIAP will:</p>
        <ul>
          <li>
            Review the deliverables for correctness, completeness, and readiness
            for validation team staffing
          </li>
          <li>
            Release the proposal for staffing to the Validator Resource Team
            (VRT)
          </li>
        </ul>
        <p>
          * The proposal is not considered &quot;Accepted&quot; until NIAP
          releases it for staffing. The project does not advance until validator
          resources are assigned.
        </p>
      </>
    ),
    timeline: "1 week",
    point: "PP1",
  },
  {
    id: 4,
    status: "Pre-Evaluation",
    definition: (
      <>
        NIAP has accepted the evaluation proposal and assigned resources to the
        project.
      </>
    ),
    initiation: <>Set by NIAP after project is assigned validator resources.</>,
    description: (
      <>
        <p>
          NIAP has received the <b>Validation Team Recommendation</b> from the
          Validator Resource Team (VRT) and the Project Assignment is issued to
          the lab and project team.
        </p>
        <p>During pre-evaluation, the NIAP validators will:</p>
        <ul>
          <li>
            Conduct the Evaluation Consistency Review (ECR) of the{" "}
            <b>check-in</b> documents provided by the Lab and enter any
            evaluation issues found in the ECR Comment tool for tracking and
            resolution.
          </li>
          <li>
            Review the EAR for correctness and completeness. If no issues are
            found, the EAR is escalated to the Entropy Subject Matter Expert
            (SME) for final review and approval. Validators act as the liaison
            between the EAR Review Team (ERT) and the Lab.
          </li>
          <li>
            Recommend the evaluation for Kickoff following final approval.
          </li>
        </ul>
      </>
    ),
    timeline:
      "3 weeks* (*2 weeks for Check-In review and 1 additional week if an EAR is required.)",
    point: "N/A",
  },
  {
    id: 5,
    status: "In-Progress",
    definition: (
      <>
        The project has successfully completed the Check-In Meeting and assigned
        a Kickoff Date.
      </>
    ),
    initiation: (
      <>
        Set by NIAP after project is Recommended for Kickoff by Senior
        Validator.
      </>
    ),
    description: (
      <>
        <p>
          NIAP has received the <b>Kickoff Recommendation</b> from the Senior
          Validator and assigned a Kickoff Date. Electronic Check-In will occur
          within three (3) business days of receipt of recommendation approval.
        </p>
        <ul>
          <li>
            NIAP issues the CCEVS-eCheck-In email detailing the evaluation to
            the Lab/Vendor/Validation Team.
          </li>
          <li>
            The project status is set from Pre-Evaluation to In-Progress (In
            Evaluation) in the database.
          </li>
          <li>
            If approved on the Check-In (F8002a) form, NIAP will include the
            product on the publicly available &quot;In Evaluation&quot; List on
            the NIAP website.
          </li>
        </ul>
        <p>
          * If the check-in meeting cannot be conducted electronically, then a
          formal meeting will occur between representatives from NIAP, the Lab,
          and the vendor within ten (10) business days in order to advance the
          project. See CICO guidance for more information.
        </p>
        <p>
          Lab evaluates the product and provides all relevant documentation to
          NIAP, which initiates Check-Out review.
        </p>
        <p>NIAP Validators will:</p>
        <ul>
          <li>
            Conduct the Evaluation Consistency Review (ECR) of the{" "}
            <b>check-out</b> documents provided by the Lab and enter any
            evaluation issues found in the ECR Comment tool for tracking and
            resolution.
          </li>
          <li>
            Review the CMVP or <b>CAVP Certificates</b> for correctness and
            completeness. If no issues are found, the cert review is assigned
            someone from the NCERT Review Team (NCRT) for final review and
            approval. An official response is released to the Lab when the
            review is complete.
          </li>
          <li>
            Upload a <b>Final Complete Check-Out package.</b> The final package
            must include from the Lab:
            <ul>
              <li>The final Security Target;</li>
              <li>Evaluation Technical Report;</li>
              <li>Publicly releasable Assurance Activity Report;</li>
              <li>Detailed Test Report;</li>
              <li>Administrative Guidance;</li>
              <li>
                any add&apos;l public guidance documents pertaining to the
                product;
              </li>
              <li>NIAP End of Evaluation Survey (Vendor Form F8004)</li>
            </ul>
          </li>
          <li>
            Submit the <b>final draft Validation Report (VR)</b> to the Vendor
            and Lab for accuracy and release approval.
            <ul>
              <li>
                The completion date of the final VR is used as the Certificate
                Issued Date of the product.
              </li>
            </ul>
          </li>
          <li>
            Recommend the evaluation for posting to the Product Compliant List
            (PCL) to NIAP.
          </li>
          <li>
            The posting recommendation automatically sets the project status
            from In-Progress to Finalizing in the database.
          </li>
        </ul>
        <p>
          * The Senior Validator cannot recommend for posting until signed{" "}
          <b>Release of Information (ROI) (F8002b) Forms</b> from the Vendor and
          Lab are received via the NIAP database system.
        </p>
      </>
    ),
    timeline:
      "90 days (Target) / 180 days (Max). Checkout review is completed within 10 business days of receipt. NIST Certificate Review may take add'l time.",
    point: "PP2, PP3, PP4, PP5, PP6, PP7",
  },
  {
    id: 6,
    status: "Finalizing",
    definition: <>The project has completed an approved Check-Out package.</>,
    initiation: (
      <>Set automatically after Senior Validator Recommends for Posting.</>
    ),
    description: (
      <>
        <p>
          NIAP has received the <b>Posting Recommendation</b> from the Senior
          Validator. The product will be published to the PCL within three (3)
          business days of receipt of recommendation approval.
        </p>
        <ul>
          <li>
            NIAP reviews the final package and check-out forms for accuracy,
            completeness. NIAP ensures documentation is free from proprietary
            information and prepares the publicly releasable files for the PCL.
          </li>
        </ul>
      </>
    ),
    timeline:
      "*NIAP will publish the product to the PCL within three (3) business days of receipt of recommendation approval.",
    point: "PP8",
  },
  {
    id: 7,
    status: "Completed",
    definition: (
      <>
        The project is listed on the Product Compliant List (PCL) on NIAP&apos;s
        public website.
      </>
    ),
    initiation: <>Set by NIAP after project is posted to the PCL.</>,
    description: (
      <>
        <p>NIAP has posted the product to the PCL.</p>
        <ul>
          <li>
            The project status automatically changes from Finalizing to
            Completed in the database.
          </li>
          <li>
            The Lab/Validators are issued an automated Posting notification.
          </li>
        </ul>
        <p>NIAP Validators will:</p>
        <ul>
          <li>Complete the Proficiency Feedback Form</li>
          <li>
            Upload the closeout records for the evaluation to the NIAP database.
          </li>
        </ul>
      </>
    ),
    timeline:
      "*NIAP will publish the product to the PCL within three (3) business days of receipt of recommendation approval.",
    point: "PP8",
  },
  {
    id: 8,
    status: "Terminated",
    definition: (
      <>
        The NIAP Director has terminated an evaluation without achieving
        successful completion.
      </>
    ),
    initiation: <>Set by NIAP at time of project termination.</>,
    description: (
      <>
        <p>
          NIAP Director will issue a CCEVS Termination Letter to the Lab and
          Vendor informing them of project termination.
        </p>
        <p>Reference CCEVS Policy Letter #25; NIAP - Policies</p>
      </>
    ),
    timeline: "N/A",
    point: "N/A",
  },
  {
    id: 9,
    status: "Withdrawn",
    definition: (
      <>
        The Lab or Vendor have withdrawn the evaluation without achieving a
        successful completion.
      </>
    ),
    initiation: (
      <>
        Set automatically after Lab submits the Request Withdrawal via the
        portal.
      </>
    ),
    description: (
      <>
        <p>
          NIAP Director will issue a CCEVS Withdrawal Letter to the Lab and
          Vendor informing them of project withdrawal.
        </p>
        <p>Reference CCEVS Policy Letter #27; NIAP - Policies</p>
      </>
    ),
    timeline: "N/A",
    point: "N/A",
  },
  {
    id: 10,
    status: "Archived",
    definition: (
      <>
        The program has archived all associated records, and the product has
        moved to the Archived Product Compliant List (APCL).
      </>
    ),
    initiation: (
      <>
        Set automatically after the product&apos;s assigned Assurance
        Maintenance Date.
      </>
    ),
    description: (
      <>
        <p>
          Reference Publication #6 Common Criteria Evaluation and Validation
          Scheme - Assurance Continuity: Guidance for Maintenance and
          Re-evaluation, Version 3.0; NIAP - Publications
        </p>
      </>
    ),
    timeline: "N/A",
    point: "N/A",
  },
];
